import React from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Box,
  Tooltip,
  Collapse,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { styled } from "@mui/system";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import UserDetailsPanel from "./UserDetailsPanel";

const EnhancedTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  cursor: "pointer",
}));

function UserTableRow({
  user,
  isExpanded,
  userDetails,
  onToggleExpand,
  onEdit,
  onDelete,
  onViewProfile,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <React.Fragment>
      <EnhancedTableRow
        onClick={() => onViewProfile(user)}
        aria-label={t("myUserManagement.viewProfileAria", {
          username: user.username || t("myUserManagement.naText"),
        })}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={(event) => onToggleExpand(user.id, event)}
            aria-label={t("myUserManagement.expandRowAria", {
              username: user.username || t("myUserManagement.naText"),
            })}
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          {user.first_name || t("myUserManagement.naText")}
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          {user.last_name || t("myUserManagement.naText")}
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          {user.username || t("myUserManagement.naText")}
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {convertUTCToLocal(user.created_at)}
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {convertUTCToLocal(user.updated_at)}
        </TableCell>
        <TableCell>
          <Chip
            variant="outlined"
            label={
              user.active
                ? t("myUserManagement.activeChip")
                : t("myUserManagement.inactiveChip")
            }
            color={user.active ? "success" : "default"}
            size="small"
            sx={{
              fontWeight: 600,
              ...(user.active && {
                bgcolor: alpha(theme.palette.success.main, 0.1),
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
              }),
            }}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={t("myUserManagement.editTooltip")}>
              <IconButton
                color="primary"
                onClick={(event) => onEdit(user.id, event)}
                aria-label={t("myUserManagement.editAria", {
                  username: user.username || t("myUserManagement.naText"),
                })}
                sx={{
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <EditNoteOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("myUserManagement.deleteTooltip")}>
              <IconButton
                color="error"
                onClick={(event) => onDelete(user.id, event)}
                aria-label={t("myUserManagement.deleteAria", {
                  username: user.username || t("myUserManagement.naText"),
                })}
                sx={{
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                  },
                }}
              >
                <DeleteForeverOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </EnhancedTableRow>

      {/* Expanded Details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <UserDetailsPanel userDetails={userDetails} />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default UserTableRow;
